/**
 * Override de UserManagement para SaaS.
 *
 * Sustituye el modal "New User" (que crea usuarios TZU nativos) por un
 * flujo de invitación SaaS: solo pide email + rol y llama a
 * POST /api/v1/orgs/{org_id}/members/invite.
 *
 * El listado de usuarios es org-scoped y consume
 * GET /api/v1/orgs/{org_id}/members.
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, Thead, Tbody, Tr, Th, Td,
  Select, Badge, Heading, HStack, VStack, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Input, useDisclosure,
  Spinner, Tabs, TabList, Tab, TabPanels, TabPanel,
  Text, Alert, AlertIcon,
} from '@chakra-ui/react';
import { getOrgMembers, inviteMember, changeOrgMemberRole, revokeOrgMember, getAuditLog } from '../services/apiClient';
import { useLocalization } from '../hooks/useLocalization';

const ROLES = ['admin', 'analyst', 'reader'];

/** Decodifica el org_id del JWT almacenado en localStorage (sin verificar firma). */
function getOrgIdFromToken() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.org_id || null;
  } catch {
    return null;
  }
}

const UserManagement = () => {
  const { t } = useLocalization();
  const tU = t?.ui?.users || {};
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState({ email: '', role: 'analyst' });
  const [inviteResult, setInviteResult] = useState(null); // { email, role }
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const orgId = getOrgIdFromToken();
      if (!orgId) {
        setUsers([]);
        return;
      }
      const data = await getOrgMembers(orgId);
      setUsers(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast({ title: tU.error_loading || 'Error loading users', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAuditLog = async () => {
    setAuditLoading(true);
    try {
      const data = await getAuditLog({ limit: 100 });
      setAuditLog(data);
    } catch (err) {
      toast({ title: 'Error loading audit log', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleOpenInvite = () => {
    setInvite({ email: '', role: 'analyst' });
    setInviteResult(null);
    onOpen();
  };

  const handleCloseInvite = () => {
    setInviteResult(null);
    onClose();
    fetchUsers();
  };

  const handleSendInvite = async () => {
    if (!invite.email) {
      toast({ title: 'Email requerido', status: 'warning', duration: 3000 });
      return;
    }
    const orgId = getOrgIdFromToken();
    if (!orgId) {
      toast({ title: 'No se pudo determinar la organización', status: 'error', duration: 4000 });
      return;
    }
    setSubmitting(true);
    try {
      await inviteMember(orgId, { email: invite.email, role: invite.role });
      setInviteResult({ email: invite.email, role: invite.role });
      toast({ title: 'Invitación enviada', description: `${invite.email} ha sido invitado como ${invite.role}`, status: 'success', duration: 4000 });
    } catch (err) {
      toast({
        title: 'Error al enviar invitación',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      const orgId = getOrgIdFromToken();
      if (!orgId) throw new Error('No se pudo determinar la organización');
      await changeOrgMemberRole(orgId, userId, role);
      toast({ title: tU.role_updated || 'Role updated', status: 'success', duration: 2000 });
      fetchUsers();
    } catch (err) {
      toast({ title: tU.error_role || 'Error updating role', description: err.response?.data?.detail || err.message, status: 'error', duration: 4000 });
    }
  };

  const handleRevoke = async (userId, email) => {
    if (!window.confirm(`${tU.confirm_delete || 'Revoke member?'} "${email}"`)) return;
    try {
      const orgId = getOrgIdFromToken();
      if (!orgId) throw new Error('No se pudo determinar la organización');
      await revokeOrgMember(orgId, userId);
      toast({ title: tU.deleted || 'Member revoked', status: 'success', duration: 2000 });
      fetchUsers();
    } catch (err) {
      toast({ title: tU.error_deleting || 'Error revoking member', description: err.response?.data?.detail || err.message, status: 'error', duration: 4000 });
    }
  };

  if (loading) return <Spinner mt={8} />;

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{tU.title || 'User Management'}</Heading>
        <Button colorScheme="blue" onClick={handleOpenInvite}>
          {tU.new_user || '+ Invitar usuario'}
        </Button>
      </HStack>

      <Tabs onChange={(idx) => { if (idx === 1) fetchAuditLog(); }}>
        <TabList>
          <Tab>{tU.users_tab || 'Users'}</Tab>
          <Tab>{tU.audit_tab || 'Audit Log'}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>{tU.username || 'Username'}</Th>
                  <Th>{tU.email || 'Email'}</Th>
                  <Th>{tU.name || 'Name'}</Th>
                  <Th>{tU.role || 'Role'}</Th>
                  <Th>{tU.status || 'Status'}</Th>
                  <Th>{tU.actions || 'Actions'}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {users.map((usr) => (
                  <Tr key={usr.user_id}>
                    <Td fontWeight="medium">{usr.email?.split('@')?.[0] || '-'}</Td>
                    <Td>{usr.email}</Td>
                    <Td>{usr.full_name}</Td>
                    <Td>
                      <Select
                        size="xs"
                        value={usr.role}
                        onChange={(e) => handleRoleChange(usr.user_id, e.target.value)}
                        width="110px"
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </Select>
                    </Td>
                    <Td>
                      <Badge colorScheme={usr.status === 'active' ? 'green' : 'red'}>
                        {usr.status === 'active' ? (tU.active || 'Active') : (tU.inactive || 'Inactive')}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleRevoke(usr.user_id, usr.email)}>
                          {tU.delete || 'Revoke'}
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TabPanel>
          <TabPanel px={0}>
            {auditLoading ? <Spinner /> : (
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Timestamp</Th>
                    <Th>Action</Th>
                    <Th>Performed By</Th>
                    <Th>Target User</Th>
                    <Th>Detail</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {auditLog.length === 0 && (
                    <Tr><Td colSpan={5}><Text color="gray.500">No audit log entries</Text></Td></Tr>
                  )}
                  {auditLog.map((entry) => (
                    <Tr key={entry.id}>
                      <Td fontSize="xs">{new Date(entry.timestamp).toLocaleString()}</Td>
                      <Td><Badge>{entry.action}</Badge></Td>
                      <Td fontSize="xs">{entry.performed_by_id}</Td>
                      <Td fontSize="xs">{entry.target_user_id || '-'}</Td>
                      <Td fontSize="xs">{entry.detail || '-'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* ── Modal de invitación ── */}
      <Modal isOpen={isOpen} onClose={handleCloseInvite} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Invitar usuario a la organización</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!inviteResult ? (
              <VStack spacing={4}>
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  El usuario recibirá un enlace para registrarse y unirse automáticamente a tu organización.
                </Alert>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="usuario@ejemplo.com"
                    value={invite.email}
                    onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Rol</FormLabel>
                  <Select
                    value={invite.role}
                    onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                  >
                    <option value="admin">Admin — gestión completa de la org</option>
                    <option value="analyst">Analyst — crear y editar proyectos</option>
                    <option value="reader">Reader — solo lectura</option>
                  </Select>
                </FormControl>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  Invitación generada para <strong>&nbsp;{inviteResult.email}&nbsp;</strong> como <strong>{inviteResult.role}</strong>.
                </Alert>
                <Box>
                  <Text fontSize="sm" color="gray.700">
                    Se envió un correo de invitación al usuario. Pídele que revise su bandeja de spam o correo no deseado si no lo encuentra en su bandeja principal.
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {!inviteResult ? (
              <>
                <Button variant="ghost" mr={3} onClick={handleCloseInvite}>Cancelar</Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSendInvite}
                  isLoading={submitting}
                  loadingText="Enviando..."
                >
                  Enviar invitación
                </Button>
              </>
            ) : (
              <Button colorScheme="blue" onClick={handleCloseInvite}>Listo</Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserManagement;
